import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, type Theme } from '@/lib/theme';
import { useAuthStore } from '@/store/auth.store';
import { useSuperAdminStore } from '@/store/super-admin.store';
import api from '@/lib/api';
import {
  Bell, Settings2, Sun, Moon, Contrast, ShieldCheck, KeyRound, LogOut,
  ChevronDown, AlertTriangle, CheckCircle2, Info, X, ArrowLeftRight,
} from 'lucide-react';

/* ── types ── */
type AlertSeverity = 'critical' | 'warning' | 'info' | 'success';
interface Alert { id: string; severity: AlertSeverity; title: string; body: string; time: string; read: boolean; }

const SEV_META: Record<AlertSeverity, { Icon: typeof AlertTriangle; color: string; bg: string; border: string }> = {
  critical: { Icon: AlertTriangle, color: '#f87171', bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.22)'   },
  warning:  { Icon: AlertTriangle, color: '#fb923c', bg: 'rgba(251,146,60,0.08)',  border: 'rgba(251,146,60,0.22)'  },
  info:     { Icon: Info,          color: '#60a5fa', bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.22)'  },
  success:  { Icon: CheckCircle2,  color: '#34d399', bg: 'rgba(52,211,153,0.08)',  border: 'rgba(52,211,153,0.22)'  },
};

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard':      'Dashboard',
  '/payments':       'Transactions',
  '/refunds':        'Refunds',
  '/analytics':      'Analytics',
  '/mandates':       'Mandates',
  '/tokenization':   'Tokenization',
  '/gateway-config': 'Gateway Config',
  '/routing':        'Smart Routing',
  '/users':          'Users',
  '/load-tester':    'Load Tester',
  '/payment-config': 'Payment Config',
  '/settings':       'Settings',
  '/settings/2fa':   'Two-Factor Auth',
  '/settings/passkeys': 'Passkeys',
};

/* ── reusable icon button ──
   darkText: true when the surface behind this control is light (light & e-ink
   themes) so text/borders should be dark-tinted rather than cyan-tinted. */
function IconBtn({
  children, active, onClick, title, darkText,
}: { children: React.ReactNode; active?: boolean; onClick: () => void; title: string; darkText: boolean }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="relative w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200"
      style={{
        background: active
          ? (darkText ? 'rgba(0,0,0,0.09)' : 'rgba(34,211,238,0.1)')
          : 'transparent',
        border: active
          ? (darkText ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(34,211,238,0.25)')
          : '1px solid transparent',
        color: active
          ? (darkText ? 'rgba(0,0,0,0.7)' : '#22d3ee')
          : (darkText ? 'rgba(0,0,0,0.4)' : 'rgba(148,218,255,0.4)'),
      }}
      onMouseEnter={(e) => {
        if (!active) {
          const el = e.currentTarget as HTMLElement;
          el.style.background = darkText ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)';
          el.style.borderColor = darkText ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)';
          el.style.color = darkText ? 'rgba(0,0,0,0.65)' : 'rgba(148,218,255,0.75)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          const el = e.currentTarget as HTMLElement;
          el.style.background = 'transparent';
          el.style.borderColor = 'transparent';
          el.style.color = active ? '' : (darkText ? 'rgba(0,0,0,0.4)' : 'rgba(148,218,255,0.4)');
        }
      }}
    >
      {children}
    </button>
  );
}

/* ── panel wrapper ── */
function Panel({
  children, width = 280, theme,
}: { children: React.ReactNode; width?: number; theme: Theme }) {
  const eink = theme === 'e-ink';
  const panelBg     = eink ? '#f0ebe0' : theme === 'light' ? '#ffffff' : '#070f20';
  const panelBorder = eink ? '1px solid rgba(0,0,0,0.13)' : theme === 'light' ? '1px solid rgba(59,130,246,0.16)' : '1px solid rgba(34,211,238,0.1)';
  const panelShadow = eink ? '0 6px 24px rgba(0,0,0,0.1)' : theme === 'light' ? '0 12px 32px rgba(15,23,42,0.12)' : '0 12px 40px rgba(0,0,0,0.55)';
  return (
    <div
      className="absolute right-0 top-full mt-2 rounded-xl overflow-hidden"
      style={{
        width,
        background: panelBg,
        border: panelBorder,
        boxShadow: panelShadow,
        zIndex: 50,
      }}
    >
      {children}
    </div>
  );
}

/* ══════════════════════════════
   TOP BAR
══════════════════════════════ */
export function TopBar({ sidebarPinned }: { sidebarPinned: boolean }) {
  const { theme, setTheme } = useTheme();
  const authStore = useAuthStore();
  const { user, clearAuth } = authStore;
  const setSaAuth = useSuperAdminStore((s) => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();

  const [openPanel, setOpenPanel] = useState<'alerts' | 'settings' | 'profile' | null>(null);
  // No real alerts feed exists yet — start empty rather than showing fabricated
  // gateway/refund alerts as if they were live.
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [switching, setSwitching] = useState(false);

  const switchToSuperAdmin = async () => {
    setSwitching(true);
    try {
      const { user: switched } = (await api.post('/auth/switch-mode')).data.data;
      authStore.setAuth(switched, authStore.accessToken!, authStore.refreshToken!);
      setSaAuth({ id: switched.id, email: switched.email, name: `${switched.firstName} ${switched.lastName}`.trim(), houseMerchantId: switched.houseMerchantId }, authStore.accessToken!);
      navigate('/super-admin', { replace: true });
    } finally {
      setSwitching(false);
    }
  };

  const rootRef = useRef<HTMLDivElement>(null);

  const eink = theme === 'e-ink';
  // Light & e-ink both sit on a light surface — use dark/black-tinted text there;
  // only dark theme gets the cyan-tinted treatment.
  const darkText = theme !== 'dark';
  const headerBg     = eink ? '#f5f0e8' : theme === 'light' ? 'rgba(242,246,255,0.9)' : 'rgba(5,12,24,0.9)';
  const headerBorder = eink ? '1px solid rgba(0,0,0,0.12)' : theme === 'light' ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(34,211,238,0.07)';
  const pageLabel = ROUTE_LABELS[location.pathname] ?? '';
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;
  const unread = alerts.filter((a) => !a.read).length;

  const toggle = (panel: typeof openPanel) =>
    setOpenPanel((prev) => (prev === panel ? null : panel));

  const markAllRead = useCallback(() =>
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true }))), []);

  const dismiss = useCallback((id: string) =>
    setAlerts((prev) => prev.filter((a) => a.id !== id)), []);

  /* close on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpenPanel(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    // Best-effort: revoke the session server-side so a token that leaked
    // before logout stops working immediately rather than riding out its
    // natural TTL. Local logout must still succeed even if this fails
    // (e.g. already offline), so it's fire-and-forget.
    api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    clearAuth();
    navigate('/login');
  };

  const sep = (
    <div style={{ margin: '4px 12px', height: 1, background: darkText ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.05)' }}/>
  );

  return (
    <header
      className="fixed top-0 right-0 z-30 flex items-center justify-between px-4"
      style={{
        left: sidebarPinned ? 240 : 0,
        height: 52,
        background: headerBg,
        backdropFilter: eink ? 'none' : 'blur(14px)',
        borderBottom: headerBorder,
        transition: 'left 0.3s ease',
      }}
    >
      {/* Left — page breadcrumb */}
      <span
        className="font-mono text-[11px] font-bold tracking-widest uppercase select-none"
        style={{ color: darkText ? 'rgba(0,0,0,0.3)' : 'rgba(148,218,255,0.25)' }}
      >
        {pageLabel && `// ${pageLabel}`}
      </span>

      {/* Right — icon cluster */}
      <div className="flex items-center gap-1" ref={rootRef}>

        {/* ── ALERTS ── */}
        <div className="relative">
          <IconBtn active={openPanel === 'alerts'} onClick={() => toggle('alerts')} title="Alerts" darkText={darkText}>
            <Bell className="w-4 h-4"/>
            {unread > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 flex items-center justify-center rounded-full text-white font-black leading-none"
                style={{ fontSize: 8, background: '#ef4444', padding: '0 3px', boxShadow: '0 0 6px rgba(239,68,68,0.7)' }}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </IconBtn>

          {openPanel === 'alerts' && (
            <Panel width={340} theme={theme}>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: darkText ? '1px solid rgba(0,0,0,0.09)' : '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5" style={{ color: darkText ? 'rgba(0,0,0,0.5)' : '#22d3ee' }}/>
                  <span className="text-[11px] font-bold tracking-wider uppercase"
                    style={{ color: darkText ? 'rgba(0,0,0,0.65)' : '#e2e8f0' }}>
                    Alerts
                  </span>
                  {unread > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-white font-black"
                      style={{ fontSize: 8, background: '#ef4444' }}>
                      {unread} new
                    </span>
                  )}
                </div>
                {unread > 0 && (
                  <button onClick={markAllRead}
                    className="text-[10px] font-semibold transition-colors"
                    style={{ color: darkText ? 'rgba(0,0,0,0.4)' : 'rgba(34,211,238,0.5)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = darkText ? 'rgba(0,0,0,0.7)' : '#22d3ee')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = darkText ? 'rgba(0,0,0,0.4)' : 'rgba(34,211,238,0.5)')}>
                    Mark all read
                  </button>
                )}
              </div>

              {/* List */}
              <div className="overflow-y-auto" style={{ maxHeight: 340 }}>
                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-10">
                    <CheckCircle2 className="w-8 h-8" style={{ color: darkText ? 'rgba(0,0,0,0.2)' : 'rgba(52,211,153,0.3)' }}/>
                    <p className="text-[11px]" style={{ color: darkText ? 'rgba(0,0,0,0.35)' : 'rgba(148,218,255,0.35)' }}>
                      All clear — no alerts
                    </p>
                  </div>
                ) : alerts.map((alert, idx) => {
                  const { Icon, color, bg, border } = SEV_META[alert.severity];
                  return (
                    <div key={alert.id}>
                      <div
                        className="flex items-start gap-3 px-4 py-3 transition-colors duration-150"
                        style={{
                          background: alert.read ? 'transparent' : (darkText ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.018)'),
                        }}
                      >
                        {/* severity dot */}
                        <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                          style={{ background: bg, border: `1px solid ${border}` }}>
                          <Icon className="w-3 h-3" style={{ color }}/>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {!alert.read && (
                              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 5px ${color}` }}/>
                            )}
                            <p className="text-[11px] font-bold truncate"
                              style={{ color: darkText ? 'rgba(0,0,0,0.75)' : '#e2e8f0' }}>
                              {alert.title}
                            </p>
                          </div>
                          <p className="text-[10px] leading-relaxed"
                            style={{ color: darkText ? 'rgba(0,0,0,0.45)' : 'rgba(148,218,255,0.5)' }}>
                            {alert.body}
                          </p>
                          <p className="text-[9px] mt-1 font-mono"
                            style={{ color: darkText ? 'rgba(0,0,0,0.3)' : 'rgba(148,218,255,0.28)' }}>
                            {alert.time}
                          </p>
                        </div>

                        <button
                          onClick={() => dismiss(alert.id)}
                          className="p-1 rounded-md shrink-0 transition-colors"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: darkText ? 'rgba(0,0,0,0.2)' : 'rgba(148,218,255,0.2)' }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = '#f87171')}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = darkText ? 'rgba(0,0,0,0.2)' : 'rgba(148,218,255,0.2)')}>
                          <X className="w-3 h-3"/>
                        </button>
                      </div>
                      {idx < alerts.length - 1 && (
                        <div style={{ margin: '0 16px', height: 1, background: darkText ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.04)' }}/>
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}
        </div>

        {/* ── SETTINGS ── */}
        <div className="relative">
          <IconBtn active={openPanel === 'settings'} onClick={() => toggle('settings')} title="Settings" darkText={darkText}>
            <Settings2 className="w-4 h-4"/>
          </IconBtn>

          {openPanel === 'settings' && (
            <Panel width={240} theme={theme}>
              {/* Theme section */}
              <div className="px-4 pt-3.5 pb-2.5">
                <p className="text-[9px] font-mono font-bold tracking-widest uppercase mb-2.5"
                  style={{ color: darkText ? 'rgba(0,0,0,0.35)' : 'rgba(148,218,255,0.35)' }}>
                  Theme
                </p>
                <div className="flex gap-2">
                  {([
                    { id: 'light',  label: 'Light', Icon: Sun,      accent: '#f59e0b' },
                    { id: 'dark',   label: 'Dark',  Icon: Moon,     accent: '#22d3ee' },
                    { id: 'e-ink',  label: 'E-Ink', Icon: Contrast, accent: '#94a3b8' },
                  ] as const).map(({ id, label, Icon, accent }) => {
                    const active = theme === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setTheme(id)}
                        className="flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-xl transition-all duration-200"
                        style={{
                          background: active ? `${accent}18` : (darkText ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'),
                          border: active ? `1.5px solid ${accent}50` : (darkText ? '1.5px solid rgba(0,0,0,0.09)' : '1.5px solid rgba(255,255,255,0.07)'),
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = darkText ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)'; }}
                        onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = darkText ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'; }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: active ? accent : (darkText ? 'rgba(0,0,0,0.4)' : 'rgba(148,218,255,0.4)') }}/>
                        <span className="text-[9px] font-mono font-bold tracking-widest"
                          style={{ color: active ? accent : (darkText ? 'rgba(0,0,0,0.45)' : 'rgba(148,218,255,0.4)') }}>
                          {label.toUpperCase()}
                        </span>
                        {active && <div className="w-4 h-0.5 rounded-full" style={{ background: accent }}/>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {sep}

              {/* Security section */}
              <div className="px-4 pt-2 pb-1.5">
                <p className="text-[9px] font-mono font-bold tracking-widest uppercase mb-1.5"
                  style={{ color: darkText ? 'rgba(0,0,0,0.35)' : 'rgba(148,218,255,0.35)' }}>
                  Security
                </p>
              </div>
              <div className="pb-2">
                <button
                  onClick={() => { setOpenPanel(null); navigate('/settings/2fa'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = darkText ? 'rgba(0,0,0,0.05)' : 'rgba(34,211,238,0.06)'}
                  onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                >
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: darkText ? 'rgba(0,0,0,0.06)' : 'rgba(34,211,238,0.1)', border: darkText ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(34,211,238,0.2)' }}>
                    <ShieldCheck className="w-3.5 h-3.5" style={{ color: darkText ? 'rgba(0,0,0,0.5)' : '#22d3ee' }}/>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold"
                      style={{ color: darkText ? 'rgba(0,0,0,0.7)' : '#cbd5e1' }}>
                      Two-Factor Auth
                    </p>
                    <p className="text-[9px] font-mono mt-0.5"
                      style={{ color: darkText ? 'rgba(0,0,0,0.35)' : 'rgba(148,218,255,0.35)' }}>
                      TOTP authenticator
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => { setOpenPanel(null); navigate('/settings/passkeys'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = darkText ? 'rgba(0,0,0,0.05)' : 'rgba(34,211,238,0.06)'}
                  onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                >
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: darkText ? 'rgba(0,0,0,0.06)' : 'rgba(34,211,238,0.1)', border: darkText ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(34,211,238,0.2)' }}>
                    <KeyRound className="w-3.5 h-3.5" style={{ color: darkText ? 'rgba(0,0,0,0.5)' : '#22d3ee' }}/>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold"
                      style={{ color: darkText ? 'rgba(0,0,0,0.7)' : '#cbd5e1' }}>
                      Passkeys
                    </p>
                    <p className="text-[9px] font-mono mt-0.5"
                      style={{ color: darkText ? 'rgba(0,0,0,0.35)' : 'rgba(148,218,255,0.35)' }}>
                      Passwordless sign-in
                    </p>
                  </div>
                </button>
              </div>

              {sep}

              <div className="py-1.5">
                <button
                  onClick={() => { setOpenPanel(null); navigate('/settings'); }}
                  className="w-full flex items-center justify-between px-4 py-2 text-left transition-all duration-150"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = darkText ? 'rgba(0,0,0,0.05)' : 'rgba(34,211,238,0.06)'}
                  onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                >
                  <span className="text-[11px] font-semibold" style={{ color: darkText ? 'rgba(0,0,0,0.6)' : '#94daff' }}>
                    All Settings
                  </span>
                  <ChevronDown className="w-3 h-3 -rotate-90" style={{ color: darkText ? 'rgba(0,0,0,0.3)' : 'rgba(148,218,255,0.3)' }}/>
                </button>
              </div>
            </Panel>
          )}
        </div>

        {/* ── PROFILE ── */}
        <div className="relative ml-1">
          <button
            onClick={() => toggle('profile')}
            className="flex items-center gap-2 pl-2 pr-2.5 py-1.5 rounded-lg transition-all duration-200"
            style={{
              background: openPanel === 'profile' ? (darkText ? 'rgba(0,0,0,0.07)' : 'rgba(34,211,238,0.08)') : 'transparent',
              border: openPanel === 'profile' ? (darkText ? '1px solid rgba(0,0,0,0.13)' : '1px solid rgba(34,211,238,0.22)') : '1px solid transparent',
            }}
            onMouseEnter={(e) => {
              if (openPanel !== 'profile') {
                const el = e.currentTarget as HTMLElement;
                el.style.background = darkText ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.04)';
                el.style.borderColor = darkText ? 'rgba(0,0,0,0.09)' : 'rgba(255,255,255,0.07)';
              }
            }}
            onMouseLeave={(e) => {
              if (openPanel !== 'profile') {
                const el = e.currentTarget as HTMLElement;
                el.style.background = 'transparent';
                el.style.borderColor = 'transparent';
              }
            }}
          >
            <div
              className="w-6 h-6 flex items-center justify-center text-white text-[10px] font-black shrink-0"
              style={{
                background: eink ? '#0d0c0a' : 'linear-gradient(135deg,#22d3ee,#a855f7)',
                borderRadius: eink ? '0.2rem' : '50%',
                boxShadow: eink ? 'none' : '0 0 8px rgba(34,211,238,0.35)',
              }}
            >
              {initials}
            </div>
            <div className="hidden sm:block text-left leading-none">
              <p className="text-[11px] font-semibold"
                style={{ color: darkText ? 'rgba(0,0,0,0.72)' : 'rgba(226,232,240,0.9)' }}>
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-[9px] font-mono uppercase tracking-wider mt-0.5"
                style={{ color: darkText ? 'rgba(0,0,0,0.38)' : 'rgba(148,218,255,0.38)' }}>
                {user?.role === 'SUPER_ADMIN' ? 'super admin' : user?.role?.replace(/_/g, ' ').toLowerCase()}
              </p>
            </div>
            <ChevronDown
              className="w-3 h-3 shrink-0 transition-transform duration-200"
              style={{
                color: darkText ? 'rgba(0,0,0,0.3)' : 'rgba(148,218,255,0.3)',
                transform: openPanel === 'profile' ? 'rotate(180deg)' : 'rotate(0)',
              }}
            />
          </button>

          {openPanel === 'profile' && (
            <Panel width={220} theme={theme}>
              {/* User info */}
              <div className="px-4 py-3.5" style={{ borderBottom: darkText ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-2.5 mb-2">
                  <div
                    className="w-8 h-8 flex items-center justify-center text-white text-[12px] font-black shrink-0"
                    style={{
                      background: eink ? '#0d0c0a' : 'linear-gradient(135deg,#22d3ee,#a855f7)',
                      borderRadius: eink ? '0.25rem' : '50%',
                      boxShadow: eink ? 'none' : '0 0 10px rgba(34,211,238,0.3)',
                    }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] font-bold truncate"
                      style={{ color: darkText ? 'rgba(0,0,0,0.75)' : '#e2e8f0' }}>
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-wider"
                      style={{ color: darkText ? 'rgba(0,0,0,0.38)' : 'rgba(148,218,255,0.38)' }}>
                      {user?.role === 'SUPER_ADMIN' ? 'super admin' : user?.role?.replace(/_/g, ' ').toLowerCase()}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] truncate"
                  style={{ color: darkText ? 'rgba(0,0,0,0.45)' : 'rgba(148,218,255,0.45)' }}>
                  {user?.email}
                </p>
              </div>

              {/* Switch to Super Admin */}
              {user?.houseMerchantId && user?.role === 'ADMIN' && (
                <div className="py-1.5" style={{ borderBottom: darkText ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.05)' }}>
                  <button
                    disabled={switching}
                    onClick={switchToSuperAdmin}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150"
                    style={{ background: 'transparent', border: 'none', cursor: switching ? 'not-allowed' : 'pointer', opacity: switching ? 0.5 : 1 }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = 'rgba(139,92,246,0.08)'}
                    onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                  >
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)' }}>
                      <ArrowLeftRight className="w-3.5 h-3.5" style={{ color: '#a78bfa' }}/>
                    </div>
                    <span className="text-[11px] font-semibold" style={{ color: '#a78bfa' }}>
                      {switching ? 'Switching…' : 'Switch to Super Admin'}
                    </span>
                  </button>
                </div>
              )}

              {/* Sign out */}
              <div className="py-1.5">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = 'rgba(248,113,113,0.08)'}
                  onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                >
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)' }}>
                    <LogOut className="w-3.5 h-3.5" style={{ color: '#f87171' }}/>
                  </div>
                  <span className="text-[11px] font-semibold" style={{ color: '#f87171' }}>Sign out</span>
                </button>
              </div>
            </Panel>
          )}
        </div>

      </div>
    </header>
  );
}
